import { makeAutoObservable } from "mobx";
import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import { ChartOfAccount, AccountType } from "../types";

// Account form data interface - matches glAccountData from api-types
export interface AccountFormData {
  id?: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  category?: string | null;
  parentAccountId?: string | null;
  isActive: boolean;
  description?: string | null;
  budgetAmount?: number | null;
}

class ChartOfAccountsStore {
  rootStore: RootStore;

  // Observables - Only UI state
  selectedAccountId: string | null = null;
  formData: AccountFormData;
  isEditMode = false;
  selectedAccountIds: string[] = [];
  filterType: "all" | AccountType = "all";
  filterStatus: "all" | "active" | "inactive" = "all";
  searchQuery = "";
  expandedNodes: string[] = [];
  isLoading = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.formData = this.getInitialFormData();

    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  // Actions
  setFilterType = (type: "all" | AccountType) => {
    this.filterType = type;
  };

  setFilterStatus = (status: "all" | "active" | "inactive") => {
    this.filterStatus = status;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  toggleNodeExpansion = (accountId: string) => {
    if (this.expandedNodes.includes(accountId)) {
      this.expandedNodes = this.expandedNodes.filter((id) => id !== accountId);
    } else {
      this.expandedNodes = [...this.expandedNodes, accountId];
    }
  };

  expandAll = () => {
    // Only expand accounts that have children (groups)
    this.expandedNodes = this.accountTree
      .filter((account) => this.hasChildren(account.id))
      .map((account) => String(account.id));
  };

  collapseAll = () => {
    this.expandedNodes = [];
  };

  openCreateForm = (parentAccountId?: string) => {
    this.formData = this.getInitialFormData();
    if (parentAccountId) {
      this.formData.parentAccountId = parentAccountId;
      // Inherit parent account type
      const parentAccount = this.accounts.find(
        (a) => a.id === parentAccountId
      );
      if (parentAccount) {
        this.formData.accountType = parentAccount.accountType;
      }
    }
    this.isEditMode = false;
    this.selectedAccountId = null;
  };

  openEditForm = (accountId: string) => {
    const account = this.chartOfAccountsStore.entities.find(
      (a: any) => String(a.id) === accountId
    ) as unknown as ChartOfAccount;
    if (!account) return;

    this.formData = {
      id: String(account.id),
      accountNumber: account.accountNumber,
      accountName: account.accountName,
      accountType: account.accountType,
      category: account.category,
      parentAccountId: account.parentAccountId,
      isActive: account.isActive,
      description: account.description || "",
      budgetAmount: account.budgetAmount,
    };
    this.isEditMode = true;
    this.selectedAccountId = accountId;
  };

  closeForm = () => {
    this.isEditMode = false;
    this.selectedAccountId = null;
    this.formData = this.getInitialFormData();
  };

  updateFormData = (
    field: keyof AccountFormData,
    value: string | number | boolean
  ) => {
    this.formData = { ...this.formData, [field]: value };
  };

  saveAccount = async () => {
    try {
      const data = {
        accountNumber: this.formData.accountNumber,
        accountName: this.formData.accountName,
        accountType: this.formData.accountType,
        category: this.formData.category,
        parentAccountId: this.formData.parentAccountId,
        isActive: this.formData.isActive,
        description: this.formData.description,
        budgetAmount: this.formData.budgetAmount,
      };

      if (this.isEditMode && this.formData.id) {
        await this.chartOfAccountsStore.patch(
          this.formData.id,
          data as unknown as Record<string, unknown>
        );
      } else {
        await this.chartOfAccountsStore.create(
          data as unknown as Record<string, unknown>
        );
      }

      this.closeForm();
    } catch (error) {
      throw error;
    }
  };

  deleteAccount = async (accountId: string) => {
    try {
      // Check if account has children
      const hasChildren = this.accounts.some(
        (a) => a.parentAccountId === accountId
      );
      if (hasChildren) {
        throw new Error(
          "Cannot delete account with child accounts. Please delete or reassign child accounts first."
        );
      }

      await this.chartOfAccountsStore.remove(accountId);
    } catch (error) {
      throw error;
    }
  };

  toggleAccountStatus = async (accountId: string) => {
    try {
      const account = this.accounts.find((a) => String(a.id) === accountId);
      if (!account) return;

      await this.chartOfAccountsStore.patch(accountId, {
        isActive: !account.isActive,
      } as unknown as Record<string, unknown>);
    } catch (error) {
      throw error;
    }
  };

  loadAccounts = async () => {
    try {
      this.isLoading = true;
      await this.chartOfAccountsStore.find({});
    } catch (error) {
      throw error;
    } finally {
      this.isLoading = false;
    }
  };

  private getInitialFormData(): AccountFormData {
    return {
      accountNumber: "",
      accountName: "",
      accountType: AccountType.ASSET,
      isActive: true,
      description: "",
      category: null,
      parentAccountId: null,
      budgetAmount: null,
    };
  }

  // Computed values
  get chartOfAccountsStore() {
    // Service store is named "gl-accounts" -> "glAccountsStore"
    return (this.rootStore as any).glAccountsStore as ServiceStore;
  }

  get accounts(): ChartOfAccount[] {
    return (
      (this.chartOfAccountsStore.entities as unknown as ChartOfAccount[]) || []
    );
  }

  get selectedAccount(): ChartOfAccount | null {
    return this.selectedAccountId
      ? (this.chartOfAccountsStore.entities.find(
          (a: any) => String(a.id) === this.selectedAccountId
        ) as unknown as ChartOfAccount)
      : null;
  }


  get filteredAccounts(): ChartOfAccount[] {
    let filtered = this.accounts;

    // Filter by account type
    if (this.filterType !== "all") {
      filtered = filtered.filter(
        (account) => account.accountType === this.filterType
      );
    }

    // Filter by active status
    if (this.filterStatus !== "all") {
      const isActive = this.filterStatus === "active";
      filtered = filtered.filter((account) => account.isActive === isActive);
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (account) =>
          account.accountNumber.toLowerCase().includes(query) ||
          account.accountName.toLowerCase().includes(query) ||
          account.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }

  /**
   * Extract base account number for grouping.
   * Examples: "1020.001" -> "1020", "1020.5" -> "1020", "1020" -> "1020"
   */
  private getBaseAccountNumber(accountNumber: string): string {
    if (!accountNumber) return "";
    const dotIndex = accountNumber.indexOf(".");
    return dotIndex > 0 ? accountNumber.substring(0, dotIndex) : accountNumber;
  }

  /**
   * Check if an account number has a sub-account suffix (e.g., .001, .5)
   */
  private isSubAccount(accountNumber: string): boolean {
    if (!accountNumber) return false;
    return accountNumber.includes(".");
  }

  /**
   * Build hierarchical tree structure by inferring hierarchy from account numbers.
   * Groups accounts by their base number (before the decimal point).
   */
  get accountTree(): ChartOfAccount[] {
    // Group accounts by base number
    const accountsByBase = new Map<string, ChartOfAccount[]>();

    this.filteredAccounts.forEach((account) => {
      const baseNum = this.getBaseAccountNumber(account.accountNumber);
      if (!accountsByBase.has(baseNum)) {
        accountsByBase.set(baseNum, []);
      }
      accountsByBase.get(baseNum)!.push(account);
    });

    // Build tree: return accounts grouped by base number
    // Only return the first account in each group as the "parent" for display
    const rootAccounts: ChartOfAccount[] = [];

    accountsByBase.forEach((accounts, baseNum) => {
      if (accounts.length === 1) {
        // Single account - show at root level
        rootAccounts.push(accounts[0]);
      } else {
        // Multiple accounts with same base - use first one as group header
        // Sort by account number to get consistent ordering
        const sorted = accounts.sort((a, b) =>
          (a.accountNumber || "").localeCompare(b.accountNumber || "")
        );
        rootAccounts.push(sorted[0]);
      }
    });

    // Sort root accounts by account number
    return rootAccounts.sort((a, b) =>
      (a.accountNumber || "").localeCompare(b.accountNumber || "")
    );
  }

  /**
   * Get child accounts (sibling accounts with same base number).
   * @param accountId - The ID of the "parent" account
   */
  getChildAccounts = (accountId: string): ChartOfAccount[] => {
    const parentAccount = this.filteredAccounts.find((a) => a.id === accountId);
    if (!parentAccount) return [];

    const baseNum = this.getBaseAccountNumber(parentAccount.accountNumber);

    // Return all accounts with the same base number, except the parent itself
    return this.filteredAccounts
      .filter((account) => {
        const accountBase = this.getBaseAccountNumber(account.accountNumber);
        return accountBase === baseNum && account.id !== accountId;
      })
      .sort((a, b) => (a.accountNumber || "").localeCompare(b.accountNumber || ""));
  };

  /**
   * Check if account has children (other accounts with same base number).
   * Only returns true if this account is the FIRST in its group (the group header).
   */
  hasChildren = (accountId: string): boolean => {
    const account = this.accounts.find((a) => a.id === accountId);
    if (!account) return false;

    const baseNum = this.getBaseAccountNumber(account.accountNumber);

    // Get all accounts with same base number
    const siblings = this.filteredAccounts.filter((a) => {
      const aBase = this.getBaseAccountNumber(a.accountNumber);
      return aBase === baseNum;
    });

    // Only 1 account in group - no children
    if (siblings.length <= 1) return false;

    // Sort siblings to find the first one (group header)
    const sorted = [...siblings].sort((a, b) =>
      (a.accountNumber || "").localeCompare(b.accountNumber || "")
    );

    // Only the first account (group header) can have children
    // Child accounts should NOT be expandable to prevent infinite recursion
    return sorted[0].id === accountId;
  };

  // Get account path (breadcrumb)
  getAccountPath = (accountId: string): ChartOfAccount[] => {
    const path: ChartOfAccount[] = [];
    let currentAccount = this.accounts.find((a) => String(a.id) === accountId);

    while (currentAccount) {
      path.unshift(currentAccount);
      currentAccount = currentAccount.parentAccountId
        ? this.accounts.find((a) => a.id === currentAccount!.parentAccountId)
        : undefined;
    }

    return path;
  };

  // Summary by account type
  get accountSummary() {
    const summary = {
      [AccountType.ASSET]: { count: 0, budgetAmount: 0 },
      [AccountType.LIABILITY]: { count: 0, budgetAmount: 0 },
      [AccountType.EQUITY]: { count: 0, budgetAmount: 0 },
      [AccountType.REVENUE]: { count: 0, budgetAmount: 0 },
      [AccountType.EXPENSE]: { count: 0, budgetAmount: 0 },
    };

    this.accounts.forEach((account) => {
      if (account.isActive && summary[account.accountType as AccountType]) {
        summary[account.accountType as AccountType].count++;
        summary[account.accountType as AccountType].budgetAmount += account.budgetAmount || 0;
      }
    });

    return summary;
  }

  get totalActiveAccounts(): number {
    return this.accounts.filter((a) => a.isActive).length;
  }

  get totalInactiveAccounts(): number {
    return this.accounts.filter((a) => !a.isActive).length;
  }
}

export default ChartOfAccountsStore;
